import { FormControl, FormErrorMessage, SelectProps } from '@chakra-ui/react'
import { BaseAddressDto, FindAddressResult } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'react-hook-form'

import { Autocomplete } from '../components/Autocomplete'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { InputGroup, InputGroupProps } from '../components/InputGroup'
import { useFindAddress, useFindStreet } from '../hooks'
import { useNameof } from '../hooks/useNameof'

export const InsureeAddressRuian = ({
  addressPrefix,
  isDisabled,
}: (InputGroupProps | SelectProps) & { addressPrefix: string }) => {
  const banameof = useNameof<BaseAddressDto>(addressPrefix)

  const addressPointName = banameof((x) => x.addressPoint)
  const streetName = banameof((x) => x.street)
  const streetNumberName = banameof((x) => x.houseNumber)
  const cityWithCityPartName = banameof((x) => x.city)
  const postCodeName = banameof((x) => x.postCode)
  const isEvidenceNumberName = banameof((x) => x.isEvidenceNumber)

  const { setValue, watch, register, getFieldState } = useFormContext()

  const street = watch(streetName)
  const streetNumber = watch(streetNumberName)
  const isEvidenceNumber = watch(isEvidenceNumberName)

  const { refetch: refetchStreets } = useFindStreet(street, { enabled: false })
  const { refetch: refetchAddress } = useFindAddress(street, streetNumber, { enabled: false })

  const { error: addressPointErrors } = getFieldState(addressPointName)
  const { error: streetNumberErrors } = getFieldState(streetNumberName)
  const { error: postCodeErrors } = getFieldState(postCodeName)
  const { error: cityWithCityPartErrors } = getFieldState(cityWithCityPartName)

  return (
    <>
      <FormGroup>
        <FormControl isRequired isInvalid={!!addressPointErrors}>
          <FormLabel>Ulice</FormLabel>

          <Autocomplete
            inputMode="text"
            htmlSize={32}
            validFromLength={2}
            value={street ?? ''}
            placeholder="Např. Hlavní"
            isDisabled={isDisabled}
            initLoad={!!street && !isDisabled}
            errors={addressPointErrors}
            onChange={(value) => {
              setValue(streetName, value)
              if (value != street) {
                setValue(streetNumberName, '')
                setValue(cityWithCityPartName, '')
                setValue(postCodeName, '')
                setValue(addressPointName, null)
              }
            }}
            refetch={() => {
              return new Promise((resolve, reject) => {
                refetchStreets()
                  .then(({ data }) =>
                    resolve(data?.data.map((x) => ({ inputText: x, value: x })) ?? []),
                  )
                  .catch((e) => reject(e))
              })
            }}
          />
        </FormControl>

        <FormControl isRequired isInvalid={!!streetNumberErrors}>
          <FormLabel>
            {isEvidenceNumber ? 'Číslo evidenční' : 'Číslo popisné nebo orientační'}
          </FormLabel>

          <Autocomplete
            inputMode="text"
            htmlSize={8}
            value={streetNumber ?? ''}
            placeholder="Např. 1234"
            isDisabled={isDisabled}
            errors={streetNumberErrors}
            initLoad={!!street && !!streetNumber && !isDisabled}
            onChange={(value) => {
              setValue(streetNumberName, value)
            }}
            onSelect={(item) => {
              const adresa = item.customData as FindAddressResult

              const newStreetNumber = adresa.cisloOrientacni
                ? `${adresa.cisloDomovni}/${adresa.cisloOrientacni}`
                : adresa.cisloDomovni

              let cityWithPart = adresa.obec
              if (adresa.castObce && adresa.castObce !== adresa.obec)
                cityWithPart += ' - ' + adresa.castObce

              setValue(streetName, adresa.ulice ?? '')
              setValue(streetNumberName, newStreetNumber ?? '')
              setValue(cityWithCityPartName, cityWithPart)
              setValue(postCodeName, adresa.psc)
              setValue(
                addressPointName,
                (adresa.adresniMisto ?? '').toString().trim() == '' ? null : adresa.adresniMisto,
              ) // obcas to padalo na chybu (nepodarilo se nasimulovat), tak preventive rusim moznost prazdenoho stringu - "property.data.address.addressPoint must be a `number` type, but the final value was: `NaN` (cast from the value `""`)."
              setValue(isEvidenceNumberName, adresa.typSo == 'č.ev.')
            }}
            refetch={() => {
              return new Promise((resolve, reject) => {
                refetchAddress()
                  .then(({ data }) =>
                    resolve(
                      (data?.data ?? [])
                        .sort((x, y) => x.obec.localeCompare(y.obec))
                        .map((x) => ({
                          inputText: x.cisloDomovni ?? x.cisloDomovni ?? '',
                          itemText: x.adresa,
                          value: x.adresniMisto?.toString() ?? '',
                          customData: x,
                        })),
                    ),
                  )
                  .catch((e) => reject(e))
              })
            }}
          />
        </FormControl>
      </FormGroup>

      <FormGroup>
        <FormControl isInvalid={!!postCodeErrors}>
          <FormLabel>PSČ</FormLabel>

          <InputGroup
            htmlSize={6}
            autoComplete="postal-code"
            placeholder="Např. 110 00"
            isDisabled={true}
            {...register(postCodeName)}
            errors={postCodeErrors}
          />
        </FormControl>

        <FormControl isInvalid={!!cityWithCityPartErrors}>
          <FormLabel>Obec</FormLabel>

          <InputGroup {...register(cityWithCityPartName)} isDisabled={true} />

          {cityWithCityPartErrors && (
            <FormErrorMessage>{cityWithCityPartErrors.message}</FormErrorMessage>
          )}
        </FormControl>
      </FormGroup>
    </>
  )
}
